<template>
  <div>
    <div class="nearby-title" :class="{ desktop: $isDesktop }">
      <template v-if="basicListing">
        <translate tag="h2">
          Nearby sites that are bookable on Pitchup.com
        </translate>
      </template>
      <template v-else>
        <translate v-if="!hasResults" tag="h2"> Nearest sites </translate>
        <i18n-t
          v-else-if="isDated"
          tag="h2"
          keypath="Nearest available for your search {within}"
        >
          <template #within>
            <translate tag="span" :tt="{ radius }">
              (within {radius, distance, short})
            </translate>
          </template>
        </i18n-t>
        <i18n-t v-else tag="h2" keypath="Nearest sites {within}">
          <template #within>
            <translate tag="span" :tt="{ radius }">
              (within {radius, distance, short})
            </translate>
          </template>
        </i18n-t>
      </template>
      <pu-search-link
        v-if="hasResults"
        class="button minimal see-all-link"
        rel="nofollow"
        :modify-filters="{
          area: point,
          placename: name,
          facets: undefined,
        }"
        category="search"
      >
        <img
          v-if="$isDesktop"
          loading="lazy"
          src="./nearby.svg"
          class="nearby-icon"
        />
        <translate> See all </translate>
      </pu-search-link>
    </div>
    <div v-if="!hasResults || isFallbackSearch" class="nearby-subhead">
      <translate v-if="!hasResults" tag="p">
        Unfortunately, we have no nearby bookable sites.
      </translate>
      <translate
        v-else-if="isFallbackSearch"
        tag="p"
        class="fallback-search-message"
      >
        No availability for your criteria.
      </translate>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'

@Component
class NearbyBookableHeader extends Vue {
  @Prop({ required: true })
  radius: { km: number; mi: number }

  @Prop({ required: true })
  isDated: boolean

  @Prop({ required: true })
  hasResults: boolean

  @Prop({ default: false })
  isFallbackSearch: boolean

  @Prop({ default: false })
  basicListing: boolean

  @Prop()
  name: string

  @Prop()
  point: { lat: number; lng: number }
}

export default toNative(NearbyBookableHeader)
</script>

<style scoped>
.nearby-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: 1em;
}

.nearby-title,
.nearby-subhead {
  padding: 1em 1.4em 0.7em;
  text-align: left;
  color: var(--colour-dark-blue);
  background: white;
}

.nearby-subhead {
  font-size: var(--body);
}

.nearby-title h2 {
  margin: 0;
  line-height: 1.15;
  font-size: 1.6em;
}

.nearby-title h2,
.nearby-title h3 {
  font-weight: normal;
  margin: 0;
}

h2 span,
h3 span {
  font-weight: normal;
  font-size: var(--small);
  white-space: nowrap;
  display: inline-block;
  vertical-align: text-top;
  line-height: 1.15;
}

.fallback-search-message {
  margin: -1em 0 1.4em;
  color: var(--colour-red);
}

.see-all-link {
  flex-shrink: 0;
  color: var(--colour-header-dark-blue);
  font-size: var(--body);
  font-weight: 600;
}

/* desktop & tablet */
.nearby-title.desktop {
  min-height: auto;
}

.nearby-title.desktop h2,
.nearby-title.desktop h3 {
  font-size: 2.25em;
  font-weight: normal;
  margin: 0;
}

.nearby-title.desktop span {
  vertical-align: initial;
}

.nearby-title.desktop .see-all-link {
  font-size: inherit;
  font-weight: bold;
  display: flex;
  align-items: center;
  column-gap: 0.5em;
  padding: 0.5em 1em 0.5em 0.75em;
}

.nearby-title.desktop .nearby-icon {
  height: 2em;
}

.nearby-title.desktop .see-all-link:hover .nearby-icon,
.nearby-title.desktop .see-all-link:focus-visible .nearby-icon {
  filter: brightness(2) opacity(0.5);
}
</style>
